import { useMutation, useQuery } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { tbImportApi } from './api'
import type { MappingPatch, TbIncludeSkippedRequest } from './types'

export function useMappings(batchId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'tb-import', batchId ?? 'none', 'mappings'),
    queryFn: () => tbImportApi.getMappings(batchId!),
    enabled: !!batchId,
  })
}

export function useUploadTb() {
  return useMutation({
    mutationFn: ({ file, asOfDate }: { file: File; asOfDate: string }) =>
      tbImportApi.upload(file, asOfDate),
  })
}

export function useAutoMap() {
  return useMutation({
    mutationFn: (batchId: string) => tbImportApi.autoMap(batchId),
  })
}

export function useSaveMappings() {
  return useMutation({
    mutationFn: ({ batchId, rows }: { batchId: string; rows: MappingPatch[] }) =>
      tbImportApi.saveMappings(batchId, rows),
  })
}

export function useValidateTb() {
  return useMutation({
    mutationFn: (batchId: string) => tbImportApi.validate(batchId),
  })
}

export function useImportTb() {
  return useMutation({
    mutationFn: ({
      batchId,
      confirmDuplicate = false,
    }: {
      batchId: string
      confirmDuplicate?: boolean
    }) => tbImportApi.runImport(batchId, { confirmDuplicate }),
  })
}

export function useIncludeSkipped() {
  return useMutation({
    mutationFn: ({ batchId, rows }: { batchId: string; rows: TbIncludeSkippedRequest[] }) =>
      tbImportApi.includeSkipped(batchId, rows),
  })
}

export function useBalanceSheet() {
  return useMutation({
    mutationFn: (asOfDate: string) => tbImportApi.balanceSheet(asOfDate),
  })
}

export function useTbDiagnostics(batchId: string | null, enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'tb-import', batchId ?? 'none', 'diagnostics'),
    queryFn: () => tbImportApi.diagnostics(batchId!),
    enabled: !!batchId && enabled,
  })
}
